import { removeMdnFormatting } from "@expert/common-ui";
import type { Logger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import type { Partner, SendExpertAssistMessage } from "@expert/shared-types";
import { Accordion, Group, Pill, Progress, Stack, Transition } from "@mantine/core";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { IconChevronDown } from "@tabler/icons-react";
import { type JSX, useCallback, useEffect, useMemo, useState } from "react";
import { MarkdownViewer } from "../../formatting";
import type { DefaultPayload } from "../api";
import { activateDevice, verifyCustomer } from "../api";
import { generatePasscode } from "../api/generatePasscode";
import { generatePasscodeForVerification } from "../api/generatePasscodeForVerification";
import type { ActivationFlowType, ClaimOrderSummaryForFrontend } from "../api/verifyCustomer";
import { verifyPasscode } from "../api/verifyPasscode";
import { verifyPasscodeForVerification } from "../api/verifyPasscodeForVerification";
import type { GuideActionOption, GuideDropdownInput, GuideOption, GuideTextInput } from "../logic-builder";
import { getRoomId, navigateBack, startSimplrSession, triggerNextStep } from "../logic-builder";
import { useDelayTracking } from "../logic-builder/useDelayTracking";
import { getGuideAnalyticData, useGuideStore } from "../store";
import type { CloseGuideSource, GuideStepVariant } from "../types";
import { AskAssist } from "./AskAssist";
import { GuideActionGroup } from "./GuideActionGroup";
import classes from "./GuideCard.module.css";
import { GuideCardInput } from "./GuideCardInput";
import { GuideCloseConfirmation } from "./GuideCloseConfirmation";
import { GuideHomeConfirmation } from "./GuideHomeConfirmation";
import { GuideMenu } from "./GuideMenu";
import { GuideNavigation } from "./GuideNavigation";
import { GuideTag } from "./GuideTag";
import { markdownOverrides } from "./markdownOverrides";

interface GuideCardProps {
    actions: GuideActionOption[];
    alternateText?: string;
    callSid?: string;
    clientChannelId?: string;
    currentStep: string;
    dropdownInput?: GuideDropdownInput;
    hasPreviousSteps: boolean;
    logger: Logger;
    onGuideClose: (source: CloseGuideSource) => void;
    opened: boolean;
    partner: Partner;
    progress: number;
    prompt?: string;
    requiresTopPadding: boolean;
    sendMessage: SendExpertAssistMessage;
    sessionId: string;
    ssoAccessToken: string;
    tag?: string;
    taskMdn: string;
    textInput?: GuideTextInput;
    variant: GuideStepVariant;
}

const validateInput = (val: string, validationRegExp: string) => {
    const regex = new RegExp(validationRegExp);
    const isValid = regex.test(val);
    return isValid;
};

interface AccountMdn {
    masked: string;
    value: string;
}

interface CustomerState {
    accountMdns: AccountMdn[];
    activationFlowType?: ActivationFlowType;
    claimSummaries: ClaimOrderSummaryForFrontend[];
    iccid: string;
    imei: string;
    phoneNumber: string;
    selectedClaimId: string;
    transactionId: string;
}

export function GuideCard({
    actions,
    alternateText,
    callSid = "",
    clientChannelId,
    currentStep,
    dropdownInput,
    hasPreviousSteps,
    logger: loggerProp,
    onGuideClose,
    opened,
    partner,
    progress,
    prompt,
    requiresTopPadding,
    sendMessage,
    sessionId,
    ssoAccessToken,
    tag,
    taskMdn,
    textInput,
    variant,
}: GuideCardProps): JSX.Element | null {
    const logger = useMemo(() => loggerProp.child({ module: "GuideCard", supportTeam: "solve" }), [loggerProp]);
    const { dispatcher } = useReactAnalytics();
    const enhancedDispatcher = callSid ? dispatcher.withIdentities({ CallSid: callSid }) : dispatcher;

    const [collapseValue, setCollapseValue] = useState<string | null>("opened");
    const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
    const [isHomeConfirmationOpen, setIsHomeConfirmationOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<GuideOption | undefined>(undefined);
    const [validationErrorMessage, setValidationErrorMessage] = useState<string | undefined>();
    const [isLoadingStep, setIsLoadingStep] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [customerState, setCustomerState] = useState<CustomerState>({
        accountMdns: [],
        activationFlowType: undefined,
        claimSummaries: [],
        iccid: "",
        imei: "",
        phoneNumber: "",
        selectedClaimId: "",
        transactionId: "",
    });
    const [verifiedCustomerToken, setVerifiedCustomerToken] = useState("");
    const [extraAuthorizedToken, setExtraAuthorizedToken] = useState("");

    const {
        errorStateSessionId,
        isErrorState,
        resetGuide,
        sessionMetadata: storedSessionMetadata, // represents local session metadata
        uiMetadata: { closeConfirmation },
        setDisableAutomaticOpen,
        setErrorStateSessionId,
    } = useGuideStore();

    const extraData = getGuideAnalyticData();
    const { completedStepCount, currentStepId, guide: flowName } = extraData;

    useDelayTracking({
        currentStepId,
        logger,
        isLoading: isLoadingStep,
        dispatcher: enhancedDispatcher,
        opened,
    });

    const handleCollapseChange = (value: string | null) => {
        logger.info(`Solve Guide | Collapse value changed to: ${value ?? "closed"}`);
        setCollapseValue(value);
        void enhancedDispatcher.dispatchBusinessEvent("Guide_CollapseChange", {
            value: value ?? "closed",
        });
    };

    const onGuideCompleted = useCallback(() => {
        logger.info({ ...extraData }, `Solve Guide | Completed guide`);
        void enhancedDispatcher.dispatchBusinessEvent(`Guide_CompletedGuide`, {
            ...extraData,
        });
        trackFullStoryEvent("Guide_CompletedGuide", {
            ...extraData,
        });
    }, [extraData, enhancedDispatcher, logger]);

    useEffect(() => {
        if (progress === 100) onGuideCompleted();
        // oxlint-disable-next-line exhaustive-deps
    }, [progress]);

    // guide was just opened/closed so toggle loading states
    // check if an existing room exists for a given session when guide opens
    useEffect(() => {
        if (getRoomId(sessionId) && opened) return;
        setIsLoadingStep(opened);
        // oxlint-disable-next-line exhaustive-deps
    }, [opened]);

    // step has loaded, so toggle loading state(s) off
    useEffect(() => {
        setIsLoadingStep(false);
    }, [currentStep]);

    const defaultPayload: DefaultPayload = {
        callSid,
        clientChannelId,
        partner,
        sessionId,
    };

    const handleVerifyPin = async () => {
        const payload = {
            ...defaultPayload,
            mdn: removeMdnFormatting(customerState.phoneNumber.replace("+1", "")),
            pin: inputValue,
        };
        const result = await verifyCustomer(ssoAccessToken, payload, logger);

        void enhancedDispatcher.dispatchBusinessEvent(`VerifyCustomer`, {
            wasSuccessful: !!result.token,
            ...extraData,
        });

        return result;
    };

    const handleGeneratePasscodeForVerification = async (targetMdn: string) => {
        const payload = {
            ...defaultPayload,
            mdn: removeMdnFormatting(customerState.phoneNumber.replace("+1", "")),
            targetMdn: removeMdnFormatting(targetMdn.replace("+1", "")),
        };
        const result = await generatePasscodeForVerification(ssoAccessToken, payload, logger);

        void enhancedDispatcher.dispatchBusinessEvent(`GeneratePasscodeForVerification`, {
            wasSuccessful: result.success,
            ...extraData,
        });
        return result;
    };

    const handleVerifyPasscodeForVerification = async (passcode: string) => {
        const payload = {
            ...defaultPayload,
            mdn: removeMdnFormatting(customerState.phoneNumber.replace("+1", "")),
            passcode,
            transactionId: customerState.transactionId,
        };
        const result = await verifyPasscodeForVerification(ssoAccessToken, payload, logger);

        void enhancedDispatcher.dispatchBusinessEvent(`VerifyPasscodeForVerification`, {
            wasSuccessful: !!result.token,
            ...extraData,
        });
        return result;
    };

    const handleGeneratePasscode = async (selectedMdn: string) => {
        const mdn = removeMdnFormatting(selectedMdn.replace("+1", ""));
        const payload = {
            ...defaultPayload,
            mdn,
        };
        const result = await generatePasscode(ssoAccessToken, payload, verifiedCustomerToken, logger);

        void enhancedDispatcher
            .withMeta({ SkipOpenSearch: true /* event contains PII, skip OpenSearch */ })
            .dispatchBusinessEvent(`GeneratePasscode`, {
                wasSuccessful: result.success,
                ...extraData,
                mdn,
            });
        return result;
    };

    const handleVerifyPasscode = async (passcode: string) => {
        const payload = {
            ...defaultPayload,
            passcode,
            transactionId: customerState.transactionId,
        };
        const result = await verifyPasscode(ssoAccessToken, payload, verifiedCustomerToken, logger);

        void enhancedDispatcher.dispatchBusinessEvent(`VerifyPasscode`, {
            wasSuccessful: !!result.token,
            ...extraData,
        });
        return result;
    };

    const handleSelectClaim = (claimId: string) => {
        const { claimSummaries } = customerState;
        const activationFlowType = "REPLACEMENT_DEVICE";
        const claimSummary = claimSummaries.find((cl) => cl.claimId === claimId);
        if (!claimSummary) return "selectClaimFailure";

        setCustomerState((s) => ({ ...s, activationFlowType: "REPLACEMENT_DEVICE", selectedClaimId: claimId }));
        const { requiresExtraAuth } = claimSummary;
        void enhancedDispatcher.dispatchBusinessEvent(`SelectClaim`, {
            activationFlowType,
            ...claimSummary,
            ...extraData,
        });

        // non-PA4 claims require extra OTP authorization before activation
        if (requiresExtraAuth && !extraAuthorizedToken) return "generatePasscode";
        else return "activateReady";
    };

    const handleActivateDevice = async () => {
        const { activationFlowType } = customerState;
        const payload = {
            ...defaultPayload,
            activationFlowType,
            claimId: customerState.selectedClaimId,
            ...(customerState.imei
                ? { manualDeviceInfo: { imei: customerState.imei, iccid: customerState.iccid } }
                : undefined),
        };
        const token = extraAuthorizedToken || verifiedCustomerToken;
        const { wasSuccessful } = await activateDevice(ssoAccessToken, payload, token, logger);

        void enhancedDispatcher.dispatchBusinessEvent(`ActivateDevice`, {
            activationFlowType,
            wasSuccessful,
            ...extraData,
        });
        return wasSuccessful;
    };

    const validateDropdown = () => {
        const isValid = !dropdownInput || !dropdownInput.required || selectedOption;
        if (!isValid) {
            setValidationErrorMessage(dropdownInput.validationError ?? "Please select an option");
            logger.info(
                { validationError: dropdownInput.validationError, ...extraData },
                "Solve Guide | Click next (navigate to next step) with dropdown validation error",
            );
        }
        return isValid;
    };

    const validateTextInput = () => {
        const isValid = !textInput || validateInput(inputValue, textInput.validationRegExp);
        if (!isValid) {
            setValidationErrorMessage(textInput.validationError);
            logger.info(
                { validationError: textInput.validationError, ...extraData },
                "Solve Guide | Click next (navigate to next step) with input validation error",
            );
        }
        return isValid;
    };

    const onNext = async (option: GuideActionOption) => {
        if (!option.skipsValidation && (!validateDropdown() || !validateTextInput())) return;

        setIsLoadingStep(true);
        const buttonClickAction = option.customAction;
        let nextStep: string | undefined;
        switch (buttonClickAction) {
            case "closeGuide": {
                setIsLoadingStep(false);
                onClose("cta");
                return;
            }
            case "goBack": {
                navigateBack({ restartFlow: false });
                return;
            }
            case "setCustomerState": {
                setCustomerState((custState) => ({ ...custState, [option.value]: inputValue }));
                nextStep = "Continue";
                break;
            }
            // TODO: refactor device-activation to live outside GuideCard https://solutonashville.atlassian.net/browse/EWS-1355
            case "verifyPin": {
                const {
                    accountMdns: unmaskedMdns,
                    activationFlowType,
                    claimSummaries,
                    token,
                    errorCode,
                } = await handleVerifyPin();

                // mask MDNs displayed in the UI
                const accountMdns = unmaskedMdns?.map((mdn) => ({
                    masked: `****${mdn.slice(-4)}`,
                    value: mdn,
                }));
                setCustomerState((s) => ({ ...s, accountMdns, activationFlowType, claimSummaries }));

                if (token) {
                    setVerifiedCustomerToken(`${token}`);
                    nextStep = "success";
                } else if (errorCode) {
                    switch (errorCode) {
                        case "SUBSCRIBER_NOT_FOUND":
                            nextStep = "subscriberNotFound";
                            break;
                        case "UNSUPPORTED_DEVICE_TYPE":
                            nextStep = "unsupportedDevice";
                            break;
                        default:
                            nextStep = "failure";
                            break;
                    }
                } else {
                    nextStep = "failure";
                }

                break;
            }
            case "generatePasscodeForVerification": {
                const { success, transactionId } = await handleGeneratePasscodeForVerification(inputValue);
                setCustomerState((s) => ({ ...s, transactionId }));
                nextStep = success ? "success" : "failure";
                break;
            }
            case "verifyPasscodeForVerification": {
                const {
                    accountMdns: unmaskedMdns,
                    activationFlowType,
                    claimSummaries,
                    token,
                    errorCode,
                } = await handleVerifyPasscodeForVerification(inputValue);

                // mask MDNs displayed in the UI
                const accountMdns = unmaskedMdns?.map((mdn) => ({
                    masked: `****${mdn.slice(-4)}`,
                    value: mdn,
                }));

                setCustomerState((s) => ({ ...s, accountMdns, activationFlowType, claimSummaries }));

                if (token) {
                    setExtraAuthorizedToken(`${token}`);
                    nextStep = "success";
                } else if (errorCode) {
                    switch (errorCode) {
                        case "SUBSCRIBER_NOT_FOUND":
                            nextStep = "subscriberNotFound";
                            break;
                        case "UNSUPPORTED_DEVICE_TYPE":
                            nextStep = "unsupportedDevice";
                            break;
                        default:
                            nextStep = "failure";
                            break;
                    }
                } else {
                    nextStep = "failure";
                }

                break;
            }
            case "setActivationTypeToNew": {
                setCustomerState((s) => ({ ...s, activationFlowType: "NEW_DEVICE" }));
                if (!extraAuthorizedToken) nextStep = "activateDifferentDeviceNeedsExtraAuth";
                break;
            }
            case "generatePasscode": {
                if (dropdownInput && !selectedOption?.value) return validateDropdown();
                const targetPhoneNumber = selectedOption?.value ?? "";
                const { success, transactionId } = await handleGeneratePasscode(targetPhoneNumber);
                setCustomerState((custState) => ({ ...custState, transactionId }));
                nextStep = success ? "success" : "failure";
                break;
            }
            case "verifyPasscode": {
                const { token } = await handleVerifyPasscode(inputValue);
                setExtraAuthorizedToken(`${token}`);
                nextStep = token ? "success" : "failure";
                break;
            }
            case "selectClaim": {
                if (!selectedOption?.value) return validateDropdown();
                const claimId = selectedOption?.value;
                const nextGuideStep = await handleSelectClaim(claimId);
                nextStep = nextGuideStep;
                break;
            }
            case "activateDevice": {
                const result = await handleActivateDevice();
                nextStep = result ? "success" : "failure";
                break;
            }
            default:
                break;
        }
        const nextStepOption = selectedOption ?? option;
        logger.info(
            { option: JSON.stringify(nextStepOption), ...extraData },
            "Solve Guide | Click next (navigate to next step)",
        );
        triggerNextStep(nextStep ?? nextStepOption.value, logger);
        setSelectedOption(undefined);
        setValidationErrorMessage(undefined);
        setInputValue("");

        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_NextStep`, {
            button: "NextStep",
            selectedOption: nextStepOption.value,
            ...extraData,
        });
    };

    const onBack = () => {
        logger.info({ ...extraData }, "Solve Guide | Click back (navigate to previous step)");
        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_PreviousStep`, {
            button: "PreviousStep",
            ...extraData,
        });
        navigateBack({ restartFlow: false });
        setIsLoadingStep(true);
    };

    const onRestart = () => {
        const eventName = isErrorState ? `RestartErrorState` : `RestartGuide`;
        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_${eventName}`, {
            button: "RestartGuide",
            ...extraData,
        });

        /*
            If the guided flow encounters the global error flow, restart the
            logic builder session with a new unique session ID based on the
            workspace session, as the previous ID is linked to the broken session.
        */
        if (isErrorState) {
            if (errorStateSessionId) {
                logger.info({ ...extraData, errorStateSessionId }, "Solve Guide | Error state - Restart new session");
                startSimplrSession({ metadata: storedSessionMetadata, sessionId: errorStateSessionId });
            } else {
                // we should never hit this block because isErrorState and
                // errorStateSessionId get set at the same time in useGuideManager
                logger.info({ ...extraData }, "Solve Guide | Error state - Restart new session");
                startSimplrSession({ metadata: storedSessionMetadata, sessionId: `${sessionId}_RESTARTED` });
                setErrorStateSessionId(sessionId);
            }
            setIsLoadingStep(true);
            return;
        }

        logger.info({ ...extraData }, "Solve Guide | Click return to home (navigate to first step)");
        setIsHomeConfirmationOpen(true);
    };

    const onRestartConfirmed = () => {
        logger.info({ ...extraData }, "Solve Guide | Click confirm return to home");
        setIsHomeConfirmationOpen(false);
        navigateBack({ restartFlow: true });
        resetGuide();
        setDisableAutomaticOpen(false);
        setIsLoadingStep(true);
    };

    const onClose = (source: "cta" | "navigation") => {
        logger.info({ ...extraData, source }, "Solve Guide | Click close guide");

        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_CloseGuide`, {
            button: "CloseGuide",
            source,
            ...extraData,
        });

        if (progress === 100 || !closeConfirmation) {
            setDisableAutomaticOpen(true);
            onGuideClose("closeButton");
        } else {
            setIsCloseConfirmationOpen(true);
        }
    };

    const onCloseConfirmed = () => {
        logger.info({ ...extraData }, `Solve Guide | Click Confirm close guide`);
        setDisableAutomaticOpen(true);
        setIsCloseConfirmationOpen(false);
        onGuideClose("closeButton");
    };

    const onDropdownClick = (option: GuideOption) => {
        logger.info({ option, ...extraData }, `Solve Guide | Selected dropdown option`);
        setValidationErrorMessage(undefined);
        setSelectedOption(option);
    };

    /** override options with dynamic options if applicable */
    const overrideDropdownOptions = (options: GuideOption[]) => {
        if (currentStepId === "GeneratePasscode")
            return customerState.accountMdns.map((item) => ({ label: item.masked, value: item.value }));
        else if (currentStepId === "SelectClaim")
            return customerState.claimSummaries.map((item) => ({
                label: `${item.deviceDescription} (${item.insuranceType})`,
                value: item.claimId,
            }));
        return options;
    };

    const onInputChange = (val: string) => {
        setInputValue(val);
    };

    // task and activate MDNs match, show some extra warning text in GuideCard copy
    const taskAndActivateMdnsMatch =
        removeMdnFormatting(customerState.phoneNumber.replace("+1", "")) ===
        removeMdnFormatting(taskMdn.replace("+1", ""));
    const contentWithOverride =
        currentStepId === "ActivateDevice" && taskAndActivateMdnsMatch && alternateText ? alternateText : currentStep;

    return (
        <>
            <Transition mounted={opened} transition="fade-down" duration={400} timingFunction="ease">
                {(styles) => (
                    <Accordion
                        chevron={<IconChevronDown viewBox="0 0 20 20" />}
                        chevronPosition="left"
                        className={classes.guideCard}
                        h="auto"
                        w="100%"
                        maw="50rem"
                        style={{ ...styles, top: requiresTopPadding ? "11rem" : "1rem" }}
                        variant={variant}
                        onChange={(value) => handleCollapseChange(value)}
                        value={collapseValue}
                    >
                        <Progress
                            bg="transparent"
                            color="success.4"
                            h="0.25rem"
                            mt="0rem"
                            value={hasPreviousSteps ? progress : 0}
                        />
                        <Stack gap="0" p="sm" pt="xs" pb="xs" justify="space-between" w="100%">
                            <Accordion.Item className={classes.accordionItem} value="opened">
                                <Group w="100%" justify="space-between">
                                    <Group align="center" gap="xs" p="0">
                                        <Accordion.Control p="0">
                                            <Pill.Group>
                                                <GuideTag>
                                                    {progress === 0 || !hasPreviousSteps
                                                        ? flowName
                                                        : `${flowName} - ${progress}%`}
                                                </GuideTag>
                                                {tag && (
                                                    <GuideTag bg="primary.4" c="var(--mantine-color-white)">
                                                        {tag}
                                                    </GuideTag>
                                                )}
                                            </Pill.Group>
                                        </Accordion.Control>
                                    </Group>
                                    <GuideNavigation
                                        hasCompletedSteps={completedStepCount > 0}
                                        isErrorState={isErrorState}
                                        isLoading={isLoadingStep}
                                        onBack={onBack}
                                        onRestart={onRestart}
                                        onClose={onClose}
                                    />
                                </Group>
                                <Accordion.Panel>
                                    <Stack gap="0rem" align="flex-start">
                                        <MarkdownViewer
                                            content={contentWithOverride}
                                            options={{ overrides: markdownOverrides }}
                                        />
                                        {dropdownInput && (
                                            <GuideMenu
                                                label={dropdownInput.label}
                                                options={overrideDropdownOptions(dropdownInput.options)}
                                                onClick={onDropdownClick}
                                                validationError={validationErrorMessage}
                                            />
                                        )}
                                        {textInput && (
                                            <GuideCardInput
                                                textInput={textInput}
                                                value={inputValue}
                                                onInput={onInputChange}
                                                validationError={validationErrorMessage}
                                            />
                                        )}
                                        {prompt && (
                                            <AskAssist
                                                callSid={callSid}
                                                extraData={extraData}
                                                prompt={prompt}
                                                sendMessage={sendMessage}
                                            />
                                        )}
                                    </Stack>
                                    <GuideActionGroup
                                        currentStep={currentStep}
                                        isLoading={isLoadingStep}
                                        options={actions}
                                        onClick={(option) => void onNext(option)}
                                    />
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Stack>
                    </Accordion>
                )}
            </Transition>
            {closeConfirmation && (
                <GuideCloseConfirmation
                    callSid={callSid}
                    extraData={extraData}
                    guideId={flowName ?? "Guided flow"}
                    isOpen={isCloseConfirmationOpen}
                    options={closeConfirmation.options}
                    title={closeConfirmation.title}
                    onCancel={() => {
                        logger.info({ ...extraData }, `Solve Guide | Cancelled close guide`);
                        setIsCloseConfirmationOpen(false);
                    }}
                    onClose={onCloseConfirmed}
                />
            )}
            <GuideHomeConfirmation
                callSid={callSid}
                extraData={extraData}
                isOpen={isHomeConfirmationOpen}
                onClose={() => {
                    logger.info({ ...extraData }, `Solve Guide | Cancelled return to home`);
                    setIsHomeConfirmationOpen(false);
                }}
                onSubmit={onRestartConfirmed}
            />
        </>
    );
}
